import axios from 'axios';

export interface PythPriceData {
  id: string;
  price: {
    price: string;
    conf: string;
    expo: number;
    publishTime: number;
  };
  emaPrice: {
    price: string;
    conf: string;
    expo: number;
    publishTime: number;
  };
}

export interface FormattedPythPrice {
  symbol: string;
  price: number;
  confidence: number;
  timestamp: number;
  emaPrice: number;
  priceChange: number;
}

class PythNetworkService {
  private baseUrl = 'https://hermes.pyth.network';
  private latestPriceEndpoint = '/v2/updates/price/latest';
  private streamEndpoint = '/v2/updates/price/stream';
  
  // Common crypto price feed IDs from Pyth Network
  private priceFeeds: { [key: string]: string } = {
    'ETH': '0xff61491a931112ddf1bd8147cd1b641375f79f5825126d665480874634fd0ace', // ETH/USD
    'BTC': '0xe62df6c8b4a85fe1a67db44dc12de5db330f7ac66b72dc658afedf0f4a415b43', // BTC/USD
    'SOL': '0xef0d8b6fda2ceba41da15d4095d1da392a0d2f8ed0c6c7bc0f4cfac8c280b56d', // SOL/USD
    'AVAX': '0x93da3352f9f1d105fdfe4971cfa80e9dd777bfc5d0f683ebb6e1294b92137bb7', // AVAX/USD
    'MATIC': '0x5de33a9112c2b700b8d30b8a3402c103578ccfa2765696471cc672bd5cf6ac52', // MATIC/USD
    'DOGE': '0xdcef50dd0a4cd2dcc17e45df1676dcb336a11a61c69df7a0299b0150c672d25c', // DOGE/USD
    'ADA': '0x2a01deaec9e51a579277b34b122399984d0bbf57e2458a7e42fecd2829867a0d', // ADA/USD
    'DOT': '0xca3eed9b267293f6595901c734c7525ce8ef49adafe8284606ceb307afa2ca5b', // DOT/USD
  };

  async getPriceData(symbols: string[]): Promise<FormattedPythPrice[]> {
    try {
      const feedIds = symbols.map(symbol => this.priceFeeds[symbol.toUpperCase()]).filter(Boolean);
      
      if (feedIds.length === 0) {
        console.warn('No valid price feeds found for symbols:', symbols);
        return [];
      }

      // Build URL with multiple ids parameters as required by Hermes v2 API
      const url = new URL(this.baseUrl + this.latestPriceEndpoint);
      feedIds.forEach(id => url.searchParams.append('ids[]', id));

      const response = await axios.get(url.toString());

      if (response.data && response.data.parsed && Array.isArray(response.data.parsed)) {
        return response.data.parsed.map((feed: any) => this.formatHermesPriceData(feed, symbols));
      }

      return [];
    } catch (error) {
      console.error('Error fetching Pyth price data:', error);
      return [];
    }
  }

  async getSinglePrice(symbol: string): Promise<FormattedPythPrice | null> {
    const prices = await this.getPriceData([symbol]);
    return prices.length > 0 ? prices[0] : null;
  }

  private formatPriceData(feed: PythPriceData, symbols: string[]): FormattedPythPrice {
    const price = parseFloat(feed.price.price) * Math.pow(10, feed.price.expo);
    const emaPrice = parseFloat(feed.emaPrice.price) * Math.pow(10, feed.emaPrice.expo);
    const confidence = parseFloat(feed.price.conf) * Math.pow(10, feed.price.expo);
    
    // Calculate price change percentage
    const priceChange = ((price - emaPrice) / emaPrice) * 100;

    // Find the symbol for this feed
    const symbol = this.findSymbolByFeedId(feed.id) || symbols[0] || 'UNKNOWN';

    return {
      symbol,
      price,
      confidence,
      timestamp: feed.price.publishTime * 1000, // Convert to milliseconds
      emaPrice,
      priceChange
    };
  }

  private formatHermesPriceData(feed: any, symbols: string[]): FormattedPythPrice {
    const symbol = this.findSymbolByFeedId(feed.id) || 'UNKNOWN';
    const price = parseFloat(feed.price.price) * Math.pow(10, feed.price.expo);
    const emaPrice = parseFloat(feed.ema_price.price) * Math.pow(10, feed.ema_price.expo);
    const confidence = parseFloat(feed.price.conf) * Math.pow(10, feed.price.expo);
    
    return {
      symbol,
      price,
      confidence,
      timestamp: feed.price.publish_time * 1000, // Convert to milliseconds
      emaPrice,
      priceChange: ((price - emaPrice) / emaPrice) * 100, // Percentage change
    };
  }

  private findSymbolByFeedId(feedId: string): string | null {
    for (const [symbol, id] of Object.entries(this.priceFeeds)) {
      if (id === feedId) {
        return symbol;
      }
    }
    return null;
  }

  // Add custom price feed
  addPriceFeed(symbol: string, feedId: string): void {
    this.priceFeeds[symbol.toUpperCase()] = feedId;
  }

  // Get available symbols
  getAvailableSymbols(): string[] {
    return Object.keys(this.priceFeeds);
  }

  // Get historical price data (simulated for demo)
  async getHistoricalData(symbol: string, hours: number = 24): Promise<Array<{timestamp: number, price: number}>> {
    try {
      const currentPrice = await this.getSinglePrice(symbol);
      if (!currentPrice) return [];

      const data = [];
      const now = Date.now();
      const interval = (hours * 60 * 60 * 1000) / 100; // 100 data points

      for (let i = 0; i < 100; i++) {
        const timestamp = now - (99 - i) * interval;
        // Simulate price variation (±5% random walk)
        const variation = (Math.random() - 0.5) * 0.1;
        const price = currentPrice.price * (1 + variation * (i / 100));
        
        data.push({
          timestamp,
          price: Math.max(0, price)
        });
      }

      return data;
    } catch (error) {
      console.error('Error generating historical data:', error);
      return [];
    }
  }

  /**
   * Create a streaming connection for real-time price updates using Server-Sent Events
   * @param symbols Array of symbols to stream
   * @param onUpdate Callback function called when new price data is received
   * @returns Function to close the stream
   */
  createPriceStream(
    symbols: string[], 
    onUpdate: (prices: FormattedPythPrice[]) => void
  ): () => void {
    const feedIds = symbols.map(symbol => this.priceFeeds[symbol.toUpperCase()]).filter(Boolean);
    
    if (feedIds.length === 0) {
      console.warn('No valid price feeds found for symbols:', symbols);
      return () => {};
    }

    // Build URL with multiple ids parameters
    const url = new URL(this.baseUrl + this.streamEndpoint);
    feedIds.forEach(id => url.searchParams.append('ids[]', id));

    const eventSource = new EventSource(url.toString());
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.parsed && Array.isArray(data.parsed)) {
          const formattedPrices = data.parsed.map((feed: any) => 
            this.formatHermesPriceData(feed, symbols)
          );
          onUpdate(formattedPrices);
        }
      } catch (error) {
        console.error('Error parsing streaming price data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Price stream error:', error);
    };

    // Return cleanup function
    return () => {
      eventSource.close();
    };
  }
}

export const pythNetwork = new PythNetworkService();